import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MoreHorizontal, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../context/AuthContext';
import { stationsApi } from '../../lib/stationsApi';
import { messageErreur } from '../../lib/erreurs';
import { libelle, nomLisible, optionsLibelles } from '../../lib/libelles';
import { formatDate, formatDateHeure, formatMontant } from '../../lib/formats';
import { apercuRetourVides, apercuVente } from '../../lib/stationAnnexCalc';
import { PageHeader } from '../../components/ui/page-header';
import { BarrePagination } from '../../components/ui/pagination-bar';
import { usePaginationServeur } from '../../hooks/usePagination';
import { useDerniereDemande } from '../../hooks/useDerniereDemande';
import { Aide } from '../../components/ui/aide';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { LoadingState } from '../../components/ui/loading-state';
import { EmptyState } from '../../components/ui/empty-state';
import { StationPicker, useDroitsStation, useStations } from '../../components/stations/StationPicker';

/**
 * Activités annexes d'une station (lot 036) : gaz en bouteilles consignées
 * (échange pleine / vide), lubrifiants, boutique, prestations (lavage, vidange).
 *
 * Le stock, les conditions (consigne) et le prix viennent du serveur ; l'aperçu
 * d'une vente passe par `lib/stationAnnexCalc.js`, qui donne les mêmes montants.
 * Une vente se fait dans le poste ouvert et compte dans ses ventes et sa caisse.
 * Les montants (historique, ventes du poste) demandent « Montants visibles » ;
 * le prix et la consigne se lisent pour vendre.
 */

const PAIEMENTS = ['CASH', 'ORANGE_MONEY', 'MOOV_MONEY', 'WAVE', 'CARD'];
const FAMILLES_ARTICLE = ['GAS', 'LUBRICANT', 'SHOP', 'SERVICE'];
const UNITES_ARTICLE = ['PIECE', 'L', 'KG'];

const CELLULES =
  'w-full border-collapse text-sm [&_td]:border-b [&_td]:border-border/60 [&_td]:px-3 [&_td]:py-2 [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-border [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-xs [&_th]:font-semibold [&_th]:text-muted-foreground [&_tr:last-child_td]:border-0';

const nouvelleCle = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

/** Quantité lisible : « 12 », « 2,5 » (millièmes au plus). */
const quantiteTexte = (valeur) =>
  valeur === null || valeur === undefined || valeur === ''
    ? '—'
    : Number(valeur).toLocaleString('fr-FR', { maximumFractionDigits: 3 });

/** Écart signé : « +3 », « −1 », « — » pour zéro. */
const ecartTexte = (valeur) => {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre) || nombre === 0) return '—';
  return `${nombre > 0 ? '+' : '−'}${quantiteTexte(Math.abs(nombre))}`;
};

const montantSigne = (valeur) => {
  const nombre = Number(valeur);
  if (!Number.isFinite(nombre) || nombre === 0) return '—';
  return nombre < 0 ? `−${formatMontant(Math.abs(nombre))}` : formatMontant(nombre);
};

/** Unité courte derrière un stock : les bouteilles se comptent « pleines ». */
const uniteStock = (article) => {
  if (article.returnable) return 'pleines';
  if (article.unit === 'L') return 'L';
  if (article.unit === 'KG') return 'kg';
  return '';
};

const optionsArticles = (articles) =>
  articles.map((article) => ({
    value: article.product_id,
    label: article.name,
    description: [
      libelle('station_famille', article.family),
      article.tracked ? `${quantiteTexte(article.stock_full)} ${uniteStock(article)}`.trim() : null,
    ]
      .filter(Boolean)
      .join(' · '),
    keywords: [libelle('station_famille', article.family)],
  }));

const maintenantLocal = () => {
  const date = new Date();
  date.setSeconds(0, 0);
  const decalage = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - decalage).toISOString().slice(0, 16);
};

const versIso = (local) => {
  if (!local) return undefined;
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const Champ = ({ id, label, aide, children, className }) => (
  <div className={`grid gap-1.5 ${className || ''}`}>
    <label htmlFor={id} className="flex items-center gap-1 text-sm font-medium text-foreground">
      {label}
      {aide}
    </label>
    {children}
  </div>
);

const Panneau = ({ titre, aide, droite, children }) => (
  <section className="rounded-xl border border-border bg-card shadow-sm">
    <header className="flex flex-wrap items-baseline justify-between gap-2 px-4 pb-2 pt-3">
      <h2 className="flex items-center gap-1 text-base font-semibold">
        {titre}
        {aide}
      </h2>
      {droite}
    </header>
    <div className="overflow-x-auto px-1 pb-3">{children}</div>
  </section>
);

const Ligne = ({ label, valeur, fort }) => (
  <div className="flex items-baseline justify-between gap-4">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className={`text-right tabular-nums ${fort ? 'text-base font-semibold' : ''}`}>{valeur}</dd>
  </div>
);

// --- Fenêtre de saisie commune -------------------------------------------------------------

/**
 * Une fenêtre, un formulaire : `soumettre` envoie, la fenêtre se ferme au succès
 * et affiche le refus du serveur (en français) sinon.
 */
const FormulaireDialog = ({ open, onOpenChange, titre, aide, soumettre, libelleBouton = 'Enregistrer', desactive, children }) => {
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  useEffect(() => {
    if (open) {
      setErreur('');
      setEnCours(false);
    }
  }, [open]);

  const envoyer = async (event) => {
    event.preventDefault();
    setErreur('');
    setEnCours(true);
    try {
      await soumettre();
      onOpenChange(false);
    } catch (probleme) {
      const message = messageErreur(probleme, 'Impossible d’enregistrer');
      setErreur(message);
      toast.error(message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1">
            {titre}
            {aide}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={envoyer} className="grid gap-4 sm:grid-cols-2">
          {children}
          {erreur && (
            <p className="text-sm font-medium text-destructive sm:col-span-2" role="alert">
              {erreur}
            </p>
          )}
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={enCours || desactive}>
              {libelleBouton}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/** Clé d'idempotence d'une saisie : renouvelée à chaque ouverture de la fenêtre. */
const useCleSaisie = (open) => {
  const [cle, setCle] = useState(nouvelleCle);
  useEffect(() => {
    if (open) setCle(nouvelleCle());
  }, [open]);
  return cle;
};

const ChoixArticle = ({ id, articles, value, onChange, disabled }) => {
  const options = useMemo(() => optionsArticles(articles), [articles]);
  return (
    <SearchableSelect
      id={id}
      value={value}
      onValueChange={onChange}
      options={options}
      aria-label="Article"
      placeholder="Choisir l’article"
      searchPlaceholder="Rechercher un article…"
      emptyMessage="Aucun article"
      disabled={disabled}
    />
  );
};

const NombreInput = ({ id, value, onChange, entier = false, min = '0', ...reste }) => (
  <Input
    id={id}
    type="number"
    inputMode={entier ? 'numeric' : 'decimal'}
    min={min}
    step={entier ? '1' : 'any'}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className="text-right tabular-nums"
    {...reste}
  />
);

// --- Vente ---------------------------------------------------------------------------------

const VenteDialog = ({ open, onOpenChange, stationId, shift, articles, produitInitial, onSaved }) => {
  const cle = useCleSaisie(open);
  const [produitId, setProduitId] = useState('');
  const [quantite, setQuantite] = useState('1');
  const [vides, setVides] = useState('0');
  const [paiement, setPaiement] = useState('CASH');
  useEffect(() => {
    if (!open) return;
    setProduitId(produitInitial || '');
    setQuantite('1');
    setVides('0');
    setPaiement('CASH');
  }, [open, produitInitial]);

  const vendables = useMemo(() => articles.filter((article) => article.is_active), [articles]);
  const article = vendables.find((ligne) => ligne.product_id === produitId) || null;
  const entier = Boolean(article && (article.returnable || article.unit === 'PIECE' || article.unit === 'SERVICE'));
  const options = useMemo(() => optionsLibelles('station_paiement', PAIEMENTS), []);

  const apercu = useMemo(() => {
    if (!article || article.unit_price === null || article.unit_price === undefined) return null;
    try {
      return apercuVente({
        quantite,
        prix: article.unit_price,
        videsRendus: vides,
        consigne: article.deposit_amount || 0,
        consignee: article.returnable,
      });
    } catch (probleme) {
      return { erreur: probleme.message };
    }
  }, [article, quantite, vides]);

  const sansPrix = Boolean(article) && (article.unit_price === null || article.unit_price === undefined);

  return (
    <FormulaireDialog
      open={open}
      onOpenChange={onOpenChange}
      titre="Vendre"
      libelleBouton="Enregistrer la vente"
      desactive={!shift || !article || !apercu || Boolean(apercu?.erreur)}
      soumettre={async () => {
        await stationsApi.annexes.sell(stationId, {
          id: cle,
          shiftId: shift?.id,
          productId: produitId,
          quantity: quantite,
          empties: article?.returnable ? vides : 0,
          paymentMethod: paiement,
        });
        toast.success('Vente enregistrée');
        onSaved();
      }}
    >
      <div className="sm:col-span-2">
        <Champ id="vente-article" label="Article">
          <ChoixArticle id="vente-article" articles={vendables} value={produitId} onChange={setProduitId} />
        </Champ>
      </div>
      <Champ id="vente-quantite" label={article?.family === 'SERVICE' ? 'Nombre de prestations' : 'Quantité'}>
        <NombreInput id="vente-quantite" value={quantite} onChange={setQuantite} entier={entier} min="0" required />
      </Champ>
      {article?.returnable && (
        <Champ
          id="vente-vides"
          label="Vides rendus"
          aide={
            <Aide titre="Vides rendus">
              Un vide rendu pour une pleine est un échange : pas de consigne. Sans vide rendu, la consigne s’ajoute au
              prix du gaz.
            </Aide>
          }
        >
          <NombreInput id="vente-vides" value={vides} onChange={setVides} entier />
        </Champ>
      )}
      <Champ id="vente-paiement" label="Paiement" className={article?.returnable ? 'sm:col-span-2' : undefined}>
        <SearchableSelect
          id="vente-paiement"
          value={paiement}
          onValueChange={setPaiement}
          options={options}
          aria-label="Paiement"
          placeholder="Choisir le paiement"
        />
      </Champ>
      {!shift && (
        <div className="sm:col-span-2">
          <Badge variant="warning">Aucun poste ouvert</Badge>
        </div>
      )}
      {sansPrix && (
        <div className="sm:col-span-2">
          <Badge variant="destructive">Sans prix</Badge>
        </div>
      )}
      {apercu?.erreur && (
        <p className="text-sm font-medium text-destructive sm:col-span-2" role="alert">
          {apercu.erreur}
        </p>
      )}
      {apercu && !apercu.erreur && (
        <dl className="grid gap-1 rounded-lg border border-border bg-muted/30 p-3 text-sm sm:col-span-2">
          <Ligne label="Prix" valeur={formatMontant(article.unit_price)} />
          <Ligne label="Montant" valeur={formatMontant(apercu.amount)} />
          {article.returnable && <Ligne label="Consigne" valeur={formatMontant(apercu.depositAmount)} />}
          <Ligne label="À encaisser" valeur={formatMontant(apercu.total)} fort />
        </dl>
      )}
    </FormulaireDialog>
  );
};

// --- Vides rendus --------------------------------------------------------------------------

const VidesDialog = ({ open, onOpenChange, stationId, shift, articles, produitInitial, onSaved }) => {
  const cle = useCleSaisie(open);
  const [produitId, setProduitId] = useState('');
  const [vides, setVides] = useState('1');
  useEffect(() => {
    if (!open) return;
    setProduitId(produitInitial || '');
    setVides('1');
  }, [open, produitInitial]);

  const consignes = useMemo(() => articles.filter((article) => article.is_active && article.returnable), [articles]);
  const article = consignes.find((ligne) => ligne.product_id === produitId) || null;
  const apercu = useMemo(() => {
    if (!article) return null;
    try {
      return apercuRetourVides({ vides, consigne: article.deposit_amount || 0 });
    } catch (probleme) {
      return { erreur: probleme.message };
    }
  }, [article, vides]);

  return (
    <FormulaireDialog
      open={open}
      onOpenChange={onOpenChange}
      titre="Vides rendus"
      aide={
        <Aide titre="Vides rendus sans achat">
          Le client rend des bouteilles vides sans en prendre de pleines : la consigne lui est remboursée en espèces,
          dans le poste ouvert.
        </Aide>
      }
      libelleBouton="Enregistrer"
      desactive={!shift || !article || !apercu || Boolean(apercu?.erreur)}
      soumettre={async () => {
        await stationsApi.annexes.returnEmpties(stationId, { id: cle, shiftId: shift?.id, productId: produitId, empties: vides });
        toast.success('Vides enregistrés');
        onSaved();
      }}
    >
      <div className="sm:col-span-2">
        <Champ id="vides-article" label="Article">
          <ChoixArticle id="vides-article" articles={consignes} value={produitId} onChange={setProduitId} />
        </Champ>
      </div>
      <Champ id="vides-nombre" label="Vides rendus">
        <NombreInput id="vides-nombre" value={vides} onChange={setVides} entier min="1" required />
      </Champ>
      {!shift && (
        <div className="self-end">
          <Badge variant="warning">Aucun poste ouvert</Badge>
        </div>
      )}
      {apercu?.erreur && (
        <p className="text-sm font-medium text-destructive sm:col-span-2" role="alert">
          {apercu.erreur}
        </p>
      )}
      {apercu && !apercu.erreur && (
        <dl className="grid gap-1 rounded-lg border border-border bg-muted/30 p-3 text-sm sm:col-span-2">
          <Ligne label="Consigne remboursée" valeur={formatMontant(-apercu.depositAmount)} fort />
        </dl>
      )}
    </FormulaireDialog>
  );
};

// --- Réception -----------------------------------------------------------------------------

const ReceptionDialog = ({ open, onOpenChange, stationId, articles, produitInitial, onSaved }) => {
  const cle = useCleSaisie(open);
  const [produitId, setProduitId] = useState('');
  const [quantite, setQuantite] = useState('');
  const [vides, setVides] = useState('0');
  const [reference, setReference] = useState('');
  useEffect(() => {
    if (!open) return;
    setProduitId(produitInitial || '');
    setQuantite('');
    setVides('0');
    setReference('');
  }, [open, produitInitial]);

  const stockes = useMemo(() => articles.filter((article) => article.is_active && article.tracked), [articles]);
  const article = stockes.find((ligne) => ligne.product_id === produitId) || null;
  const entier = Boolean(article && (article.returnable || article.unit === 'PIECE'));
  const rien = !(Number(quantite) > 0) && !(Number(vides) > 0);

  return (
    <FormulaireDialog
      open={open}
      onOpenChange={onOpenChange}
      titre="Réception"
      libelleBouton="Enregistrer la réception"
      desactive={!article || rien}
      soumettre={async () => {
        await stationsApi.annexes.receive(stationId, {
          id: cle,
          productId: produitId,
          quantity: quantite,
          empties: article?.returnable ? vides : 0,
          reference,
        });
        toast.success('Réception enregistrée');
        onSaved();
      }}
    >
      <div className="sm:col-span-2">
        <Champ id="reception-article" label="Article">
          <ChoixArticle id="reception-article" articles={stockes} value={produitId} onChange={setProduitId} />
        </Champ>
      </div>
      <Champ id="reception-quantite" label={article?.returnable ? 'Pleines reçues' : 'Quantité reçue'}>
        <NombreInput id="reception-quantite" value={quantite} onChange={setQuantite} entier={entier} />
      </Champ>
      {article?.returnable && (
        <Champ
          id="reception-vides"
          label="Vides rendus au fournisseur"
          aide={
            <Aide titre="Vides rendus au fournisseur">
              Les bouteilles vides reprises par le livreur sortent du stock de vides.
            </Aide>
          }
        >
          <NombreInput id="reception-vides" value={vides} onChange={setVides} entier />
        </Champ>
      )}
      <div className="sm:col-span-2">
        <Champ id="reception-reference" label="Bon de livraison ou fournisseur">
          <Input id="reception-reference" value={reference} onChange={(event) => setReference(event.target.value)} maxLength={120} />
        </Champ>
      </div>
    </FormulaireDialog>
  );
};

// --- Correction de stock -------------------------------------------------------------------

const CorrectionDialog = ({ open, onOpenChange, stationId, articles, produitInitial, onSaved }) => {
  const cle = useCleSaisie(open);
  const [produitId, setProduitId] = useState('');
  const [ecart, setEcart] = useState('');
  const [ecartVides, setEcartVides] = useState('0');
  const [motif, setMotif] = useState('');
  useEffect(() => {
    if (!open) return;
    setProduitId(produitInitial || '');
    setEcart('');
    setEcartVides('0');
    setMotif('');
  }, [open, produitInitial]);

  const stockes = useMemo(() => articles.filter((article) => article.tracked), [articles]);
  const article = stockes.find((ligne) => ligne.product_id === produitId) || null;
  const entier = Boolean(article && (article.returnable || article.unit === 'PIECE'));
  const rien = !Number(ecart) && !Number(ecartVides);

  return (
    <FormulaireDialog
      open={open}
      onOpenChange={onOpenChange}
      titre="Corriger le stock"
      aide={
        <Aide titre="Écart">
          Saisissez l’écart par rapport au stock affiché : −2 pour deux articles de moins, +3 pour trois de plus. Le
          motif est conservé.
        </Aide>
      }
      desactive={!article || rien || motif.trim().length < 3}
      soumettre={async () => {
        await stationsApi.annexes.adjust(stationId, {
          id: cle,
          productId: produitId,
          quantity: ecart,
          empties: article?.returnable ? ecartVides : 0,
          reason: motif.trim(),
        });
        toast.success('Stock corrigé');
        onSaved();
      }}
    >
      <div className="sm:col-span-2">
        <Champ id="correction-article" label="Article">
          <ChoixArticle id="correction-article" articles={stockes} value={produitId} onChange={setProduitId} />
        </Champ>
      </div>
      <Champ id="correction-ecart" label={article?.returnable ? 'Écart des pleines' : 'Écart'}>
        <NombreInput id="correction-ecart" value={ecart} onChange={setEcart} entier={entier} min="-100000" />
      </Champ>
      {article?.returnable && (
        <Champ id="correction-vides" label="Écart des vides">
          <NombreInput id="correction-vides" value={ecartVides} onChange={setEcartVides} entier min="-100000" />
        </Champ>
      )}
      <div className="sm:col-span-2">
        <Champ id="correction-motif" label="Motif">
          <Input id="correction-motif" value={motif} onChange={(event) => setMotif(event.target.value)} maxLength={500} required />
        </Champ>
      </div>
    </FormulaireDialog>
  );
};

// --- Article (création, conditions) -----------------------------------------------------------

const ARTICLE_VIDE = { name: '', family: 'GAS', unit: 'PIECE', returnable: false, deposit_amount: '', subsidized: false, reorder_level: '' };

const ArticleDialog = ({ open, onOpenChange, article, onSaved }) => {
  const cle = useCleSaisie(open);
  const [form, setForm] = useState(ARTICLE_VIDE);
  useEffect(() => {
    if (!open) return;
    setForm(
      article
        ? {
            name: article.name,
            family: article.family,
            unit: article.unit,
            returnable: article.returnable,
            deposit_amount: article.deposit_amount ?? '',
            subsidized: article.subsidized,
            reorder_level: article.reorder_level ?? '',
          }
        : ARTICLE_VIDE,
    );
  }, [open, article]);

  const changer = (champ) => (valeur) => setForm((avant) => ({ ...avant, [champ]: valeur?.target ? valeur.target.value : valeur }));
  const familles = useMemo(() => optionsLibelles('station_famille', FAMILLES_ARTICLE), []);
  const unites = useMemo(() => optionsLibelles('station_unite', UNITES_ARTICLE), []);
  const prestation = form.family === 'SERVICE';
  const peutEtreConsigne = !prestation && form.unit === 'PIECE';

  const changerFamille = (famille) =>
    setForm((avant) => ({
      ...avant,
      family: famille,
      unit: famille === 'SERVICE' ? 'SERVICE' : avant.unit === 'SERVICE' ? 'PIECE' : avant.unit,
      returnable: famille === 'SERVICE' ? false : avant.returnable,
    }));

  const corps = () => ({
    returnable: peutEtreConsigne ? form.returnable : false,
    deposit_amount: peutEtreConsigne && form.returnable ? Number(form.deposit_amount || 0) : 0,
    subsidized: prestation ? false : form.subsidized,
    reorder_level: prestation || form.reorder_level === '' ? null : Number(form.reorder_level),
  });

  return (
    <FormulaireDialog
      open={open}
      onOpenChange={onOpenChange}
      titre={article ? 'Conditions de l’article' : 'Nouvel article'}
      desactive={!form.name.trim()}
      soumettre={async () => {
        if (article) {
          await stationsApi.annexes.setTerms(article.product_id, corps());
          toast.success('Conditions enregistrées');
        } else {
          await stationsApi.annexes.createItem({
            id: cle,
            name: form.name.trim(),
            family: form.family,
            unit: prestation ? 'SERVICE' : form.unit,
            ...corps(),
          });
          toast.success('Article créé');
        }
        onSaved();
      }}
    >
      <div className="sm:col-span-2">
        <Champ id="article-nom" label="Nom">
          <Input id="article-nom" value={form.name} onChange={changer('name')} maxLength={100} required disabled={Boolean(article)} />
        </Champ>
      </div>
      <Champ id="article-famille" label="Type">
        <SearchableSelect
          id="article-famille"
          value={form.family}
          onValueChange={changerFamille}
          options={familles}
          aria-label="Type"
          disabled={Boolean(article)}
        />
      </Champ>
      {!prestation && (
        <Champ id="article-unite" label="Unité de vente">
          <SearchableSelect
            id="article-unite"
            value={form.unit}
            onValueChange={changer('unit')}
            options={unites}
            aria-label="Unité"
            disabled={Boolean(article)}
          />
        </Champ>
      )}
      {peutEtreConsigne && (
        <div className="flex items-center gap-2 sm:col-span-2">
          <Switch id="article-consigne" checked={form.returnable} onCheckedChange={changer('returnable')} />
          <label htmlFor="article-consigne" className="flex items-center gap-1 text-sm font-medium">
            Bouteille consignée
            <Aide titre="Bouteille consignée">
              Le stock se tient en pleines et en vides. Un vide rendu en échange ne paie pas de consigne ; sans vide
              rendu, la consigne s’ajoute au prix.
            </Aide>
          </label>
        </div>
      )}
      {peutEtreConsigne && form.returnable && (
        <Champ id="article-depot" label="Consigne par bouteille (FCFA)">
          <NombreInput id="article-depot" value={form.deposit_amount} onChange={changer('deposit_amount')} entier />
        </Champ>
      )}
      {!prestation && (
        <Champ id="article-seuil" label="Seuil de réapprovisionnement">
          <NombreInput id="article-seuil" value={form.reorder_level} onChange={changer('reorder_level')} entier={form.unit === 'PIECE'} />
        </Champ>
      )}
      {!prestation && (
        <div className="flex items-center gap-2 sm:col-span-2">
          <Switch id="article-subventionne" checked={form.subsidized} onCheckedChange={changer('subsidized')} />
          <label htmlFor="article-subventionne" className="text-sm font-medium">
            Prix subventionné
          </label>
        </div>
      )}
    </FormulaireDialog>
  );
};

// --- Prix d'un article ---------------------------------------------------------------------

const PrixDialog = ({ open, onOpenChange, stationId, article, onSaved }) => {
  const [prix, setPrix] = useState('');
  const [effet, setEffet] = useState(maintenantLocal());
  useEffect(() => {
    if (!open) return;
    setPrix('');
    setEffet(maintenantLocal());
  }, [open]);

  return (
    <FormulaireDialog
      open={open}
      onOpenChange={onOpenChange}
      titre={article ? `Prix · ${article.name}` : 'Prix'}
      libelleBouton="Publier"
      desactive={!article || !prix}
      soumettre={async () => {
        await stationsApi.prices.publish(stationId, { productId: article.product_id, unitPrice: Number(prix), effectiveAt: versIso(effet) });
        toast.success('Prix publié');
        onSaved();
      }}
    >
      <Champ id="annexe-prix" label="Prix (FCFA)">
        <NombreInput id="annexe-prix" value={prix} onChange={setPrix} entier min="1" required />
      </Champ>
      <Champ id="annexe-prix-effet" label="À partir du">
        <Input id="annexe-prix-effet" type="datetime-local" value={effet} onChange={(event) => setEffet(event.target.value)} required />
      </Champ>
    </FormulaireDialog>
  );
};

// --- Page -----------------------------------------------------------------------------------

const AUCUN_DIALOGUE = { type: null, produitId: null };

const StationAnnexesPage = () => {
  const droits = useDroitsStation();
  const { canSeeMoney } = useAuth();
  const { stations } = useStations();
  const [stationId, setStationId] = useState('');
  const station = stations.find((ligne) => ligne.id === stationId) || null;

  const [donnees, setDonnees] = useState(null);
  const [mouvements, setMouvements] = useState({ lignes: [], montantsMasques: false, total: 0 });
  const [totaux, setTotaux] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [onglet, setOnglet] = useState('stock');
  const [dialogue, setDialogue] = useState(AUCUN_DIALOGUE);

  // L'historique se lit page par page: la page vit dans l'adresse, le serveur donne le total.
  const paginationMouvements = usePaginationServeur(mouvements.total, { cle: 'mouvements', reinitialiser: stationId });
  const { skip: sautMouvements, limit: limiteMouvements } = paginationMouvements;

  const nouvelleDemande = useDerniereDemande();
  const chargerMouvements = useCallback(async () => {
    if (!stationId) return;
    const perimee = nouvelleDemande();
    try {
      const page = await stationsApi.annexes.movements(stationId, { limit: limiteMouvements, skip: sautMouvements });
      if (perimee()) return;
      setMouvements(page);
    } catch (probleme) {
      if (perimee()) return;
      setMouvements({ lignes: [], montantsMasques: false, total: 0 });
      toast.error(messageErreur(probleme, 'Impossible de charger les mouvements'));
    }
  }, [stationId, sautMouvements, limiteMouvements, nouvelleDemande]);

  useEffect(() => {
    chargerMouvements();
  }, [chargerMouvements]);

  const charger = useCallback(
    async (id) => {
      if (!id) return;
      setChargement(true);
      const [lesArticles] = await Promise.allSettled([stationsApi.annexes.items(id)]);
      if (lesArticles.status === 'rejected') toast.error(messageErreur(lesArticles.reason, 'Impossible de charger les articles'));
      const articles = lesArticles.status === 'fulfilled' ? lesArticles.value : null;
      setDonnees(articles);
      setTotaux(null);
      if (articles?.shift?.id && canSeeMoney) {
        try {
          setTotaux(await stationsApi.annexes.totals(id, articles.shift.id));
        } catch (probleme) {
          toast.error(messageErreur(probleme, 'Impossible de charger les ventes du poste'));
        }
      }
      setChargement(false);
    },
    [canSeeMoney],
  );

  useEffect(() => {
    charger(stationId);
  }, [stationId, charger]);

  const articles = useMemo(() => donnees?.items || [], [donnees]);
  const shift = donnees?.shift || null;
  const montantsCaches = Boolean(donnees?.money_hidden);
  const peutEcrire = droits.peutEcrire('station_annexes') && Boolean(station);
  const peutPrix = droits.peutEcrire('prices') && Boolean(station) && !montantsCaches;
  const sousLeSeuil = articles.filter((article) => article.below_reorder).length;
  const articleDuDialogue = articles.find((article) => article.product_id === dialogue.produitId) || null;

  const apresSaisie = useCallback(() => {
    chargerMouvements();
    return charger(stationId);
  }, [charger, chargerMouvements, stationId]);
  const ouvrir = (type, produitId = null) => setDialogue({ type, produitId });
  const fermer = (ouvert) => {
    if (!ouvert) setDialogue(AUCUN_DIALOGUE);
  };

  const nomsArticles = useMemo(() => Object.fromEntries(articles.map((article) => [article.product_id, article.name])), [articles]);

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Stations"
        title="Activités annexes"
        description="Gaz en bouteilles consignées, lubrifiants, boutique et prestations de la station."
        meta={station ? nomLisible(station.name, station.code) : undefined}
        stats={[
          { label: 'Articles', valeur: articles.length },
          totaux && !totaux.money_hidden && { label: 'Ventes du poste', valeur: formatMontant(totaux.sales_amount) },
        ].filter(Boolean)}
        alertes={[
          station && !chargement && donnees && !shift && peutEcrire && {
            niveau: 'attention',
            texte: 'Aucun poste n’est ouvert : les ventes sont impossibles tant qu’un poste n’est pas ouvert.',
            court: 'Aucun poste ouvert',
          },
          sousLeSeuil > 0 && {
            niveau: 'attention',
            texte: `${sousLeSeuil} article${sousLeSeuil > 1 ? 's' : ''} sous le seuil de réapprovisionnement`,
            court: 'Sous le seuil',
            nombre: sousLeSeuil,
          },
        ].filter(Boolean)}
        actions={
          <>
            <StationPicker stations={stations} value={stationId} onChange={setStationId} />
            {peutEcrire && (
              <>
                <Button type="button" onClick={() => ouvrir('vente')}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Vendre
                </Button>
                <Button type="button" variant="outline" onClick={() => ouvrir('reception')}>
                  Réception
                </Button>
                <Button type="button" variant="outline" onClick={() => ouvrir('article')}>
                  Nouvel article
                </Button>
              </>
            )}
          </>
        }
      />

      {!station ? (
        <EmptyState variant="empty" title="Choisissez une station" className="border-0" />
      ) : chargement && !donnees ? (
        <LoadingState shape="table" />
      ) : articles.length === 0 ? (
        <EmptyState
          variant="empty"
          title="Aucun article"
          className="border-0"
          action={
            peutEcrire ? (
              <Button type="button" onClick={() => ouvrir('article')}>
                <Plus className="h-4 w-4" aria-hidden="true" />
                Nouvel article
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Tabs value={onglet} onValueChange={setOnglet}>
          <TabsList className="h-auto flex-wrap justify-start">
            <TabsTrigger value="stock">Stock</TabsTrigger>
            <TabsTrigger value="mouvements">Mouvements</TabsTrigger>
            {!montantsCaches && <TabsTrigger value="tarifs">Tarifs</TabsTrigger>}
          </TabsList>

          <TabsContent value="stock" className="grid gap-4">
            <Panneau titre="Stock">
              <table className={CELLULES}>
                <thead>
                  <tr>
                    <th className="text-left">Article</th>
                    <th className="text-right">En stock</th>
                    <th className="text-right">Vides</th>
                    <th className="text-right">Chez les clients</th>
                    {!montantsCaches && <th className="text-right">Prix</th>}
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {articles.map((article) => (
                    <tr key={article.product_id} className={article.below_reorder ? 'bg-flame/5' : undefined}>
                      <td>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{article.name}</span>
                          {article.subsidized && <Badge variant="info">Subventionné</Badge>}
                          {article.below_reorder && <Badge variant="warning">Sous le seuil</Badge>}
                          {!article.is_active && <Badge variant="muted">Retiré</Badge>}
                        </div>
                        <span className="text-xs text-muted-foreground">{libelle('station_famille', article.family)}</span>
                      </td>
                      <td className="text-right tabular-nums">
                        {article.tracked ? `${quantiteTexte(article.stock_full)} ${uniteStock(article)}`.trim() : '—'}
                      </td>
                      <td className="text-right tabular-nums">{article.returnable ? quantiteTexte(article.stock_empty) : '—'}</td>
                      <td className="text-right tabular-nums">{article.returnable ? quantiteTexte(article.outstanding) : '—'}</td>
                      {!montantsCaches && (
                        <td className="text-right font-semibold tabular-nums">
                          {article.unit_price !== null && article.unit_price !== undefined ? (
                            formatMontant(article.unit_price)
                          ) : (
                            <Badge variant="destructive">Sans prix</Badge>
                          )}
                        </td>
                      )}
                      <td className="whitespace-nowrap text-right">
                        {peutEcrire && (
                          <div className="flex items-center justify-end gap-1">
                            {article.is_active && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={!shift}
                                onClick={() => ouvrir('vente', article.product_id)}
                              >
                                Vendre
                              </Button>
                            )}
                            {article.tracked && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button type="button" variant="ghost" size="sm" aria-label={`Autres actions : ${article.name}`}>
                                    <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {article.is_active && (
                                    <DropdownMenuItem onSelect={() => ouvrir('reception', article.product_id)}>Recevoir</DropdownMenuItem>
                                  )}
                                  {article.is_active && article.returnable && (
                                    <DropdownMenuItem disabled={!shift} onSelect={() => ouvrir('vides', article.product_id)}>
                                      Vides rendus
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onSelect={() => ouvrir('correction', article.product_id)}>
                                    Corriger le stock
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panneau>

            {totaux && !totaux.money_hidden && (
              <Panneau
                titre="Ventes du poste"
                droite={<span className="text-xs text-muted-foreground tabular-nums">Total {formatMontant(totaux.sales_amount)}</span>}
              >
                {totaux.lines.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-muted-foreground">Aucune vente dans ce poste</p>
                ) : (
                  <table className={CELLULES}>
                    <thead>
                      <tr>
                        <th className="text-left">Article</th>
                        <th className="text-right">Vendus</th>
                        <th className="text-right">Vides échangés</th>
                        <th className="text-right">Montant</th>
                        <th className="text-right">Consigne</th>
                      </tr>
                    </thead>
                    <tbody>
                      {totaux.lines.map((ligne) => (
                        <tr key={ligne.product_id}>
                          <td className="font-medium">{ligne.product_name}</td>
                          <td className="text-right tabular-nums">{quantiteTexte(ligne.quantity)}</td>
                          <td className="text-right tabular-nums">{quantiteTexte(ligne.empties_exchanged)}</td>
                          <td className="text-right tabular-nums">{formatMontant(ligne.amount)}</td>
                          <td className="text-right tabular-nums">{montantSigne(ligne.deposit_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-semibold">
                        <td colSpan={3}>Espèces</td>
                        <td colSpan={2} className="text-right tabular-nums">
                          {formatMontant(totaux.cash_amount)}
                        </td>
                      </tr>
                      <tr className="font-semibold">
                        <td colSpan={3}>Hors espèces</td>
                        <td colSpan={2} className="text-right tabular-nums">
                          {formatMontant(totaux.non_cash_amount)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
              </Panneau>
            )}
          </TabsContent>

          <TabsContent value="mouvements">
            <Panneau titre="Mouvements">
              {mouvements.lignes.length === 0 ? (
                <p className="px-3 py-2 text-sm text-muted-foreground">Aucun mouvement</p>
              ) : (
                <table className={CELLULES}>
                  <thead>
                    <tr>
                      <th className="text-left">Date</th>
                      <th className="text-left">Article</th>
                      <th className="text-left">Type</th>
                      <th className="text-right">Quantité</th>
                      <th className="text-right">Vides</th>
                      {!mouvements.montantsMasques && <th className="text-right">Encaissé</th>}
                      <th className="text-left">Paiement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mouvements.lignes.map((ligne) => (
                      <tr key={ligne.id}>
                        <td className="whitespace-nowrap">{formatDateHeure(ligne.occurred_at)}</td>
                        <td className="font-medium">{ligne.product_name || nomsArticles[ligne.product_id] || '—'}</td>
                        <td>{libelle('station_article_mouvement', ligne.kind)}</td>
                        <td className="text-right tabular-nums">{ecartTexte(ligne.quantity)}</td>
                        <td className="text-right tabular-nums">{ecartTexte(ligne.empties)}</td>
                        {!mouvements.montantsMasques && (
                          <td className="text-right tabular-nums">{montantSigne(Number(ligne.amount || 0) + Number(ligne.deposit_amount || 0))}</td>
                        )}
                        <td>{ligne.payment_method ? libelle('station_paiement', ligne.payment_method) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panneau>
            {mouvements.total > 0 ? <BarrePagination pagination={paginationMouvements} nom="mouvements" /> : null}
          </TabsContent>

          {!montantsCaches && (
            <TabsContent value="tarifs">
              <Panneau titre="Tarifs">
                <table className={CELLULES}>
                  <thead>
                    <tr>
                      <th className="text-left">Article</th>
                      <th className="text-right">Prix</th>
                      <th className="text-left">Depuis</th>
                      <th className="text-right">Consigne</th>
                      <th className="text-right">Seuil</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {articles
                      .filter((article) => article.is_active)
                      .map((article) => (
                        <tr key={article.product_id}>
                          <td>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium">{article.name}</span>
                              {article.subsidized && <Badge variant="info">Subventionné</Badge>}
                            </div>
                            <span className="text-xs text-muted-foreground">{libelle('station_famille', article.family)}</span>
                          </td>
                          <td className="text-right font-semibold tabular-nums">
                            {article.unit_price !== null && article.unit_price !== undefined ? (
                              formatMontant(article.unit_price)
                            ) : (
                              <Badge variant="destructive">Sans prix</Badge>
                            )}
                          </td>
                          <td className="whitespace-nowrap">{article.price_effective_at ? formatDate(article.price_effective_at) : '—'}</td>
                          <td className="text-right tabular-nums">{article.returnable ? formatMontant(article.deposit_amount) : '—'}</td>
                          <td className="text-right tabular-nums">{article.reorder_level !== null && article.reorder_level !== undefined ? quantiteTexte(article.reorder_level) : '—'}</td>
                          <td className="whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-1">
                              {peutPrix && (
                                <Button type="button" variant="outline" size="sm" onClick={() => ouvrir('prix', article.product_id)}>
                                  Changer le prix
                                </Button>
                              )}
                              {peutEcrire && (
                                <Button type="button" variant="outline" size="sm" onClick={() => ouvrir('conditions', article.product_id)}>
                                  Conditions
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </Panneau>
            </TabsContent>
          )}
        </Tabs>
      )}

      {station && (
        <>
          <VenteDialog
            open={dialogue.type === 'vente'}
            onOpenChange={fermer}
            stationId={station.id}
            shift={shift}
            articles={articles}
            produitInitial={dialogue.produitId}
            onSaved={apresSaisie}
          />
          <VidesDialog
            open={dialogue.type === 'vides'}
            onOpenChange={fermer}
            stationId={station.id}
            shift={shift}
            articles={articles}
            produitInitial={dialogue.produitId}
            onSaved={apresSaisie}
          />
          <ReceptionDialog
            open={dialogue.type === 'reception'}
            onOpenChange={fermer}
            stationId={station.id}
            articles={articles}
            produitInitial={dialogue.produitId}
            onSaved={apresSaisie}
          />
          <CorrectionDialog
            open={dialogue.type === 'correction'}
            onOpenChange={fermer}
            stationId={station.id}
            articles={articles}
            produitInitial={dialogue.produitId}
            onSaved={apresSaisie}
          />
          <ArticleDialog
            open={dialogue.type === 'article' || dialogue.type === 'conditions'}
            onOpenChange={fermer}
            article={dialogue.type === 'conditions' ? articleDuDialogue : null}
            onSaved={apresSaisie}
          />
          <PrixDialog
            open={dialogue.type === 'prix'}
            onOpenChange={fermer}
            stationId={station.id}
            article={articleDuDialogue}
            onSaved={apresSaisie}
          />
        </>
      )}
    </div>
  );
};

export default StationAnnexesPage;
